import { DateInput, NumberInput, Text } from './FieldComponents';

const toMoneyNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const formatMoney = (value) => toMoneyNumber(value).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const formatStatus = (value) => String(value || 'draft')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());

const getInvoiceBalance = (invoice) => {
    const hasInvoiceTotal = invoice?.total !== null && invoice?.total !== undefined && invoice.total !== '';
    const hasTotalPaid = invoice?.total_paid !== null && invoice?.total_paid !== undefined && invoice.total_paid !== '';
    if (hasInvoiceTotal && hasTotalPaid) {
        return Math.max(0, toMoneyNumber(invoice.total) - toMoneyNumber(invoice.total_paid));
    }
    const storedBalance = invoice?.balance;
    if (storedBalance !== null && storedBalance !== undefined && storedBalance !== '') {
        return Math.max(0, toMoneyNumber(storedBalance));
    }
    return Math.max(0, toMoneyNumber(invoice?.total) - toMoneyNumber(invoice?.total_paid));
};

export const paymentInvoiceIsAllocatable = (invoice, payment = {}) => {
    const status = String(invoice?.status || '').trim().toLowerCase();
    if (['void', 'voided', 'cancelled', 'canceled'].includes(status)) return false;
    const existingAllocation = (Array.isArray(payment?.allocations) ? payment.allocations : [])
        .filter(allocation => Number(allocation?.invoice_id) === Number(invoice?.id))
        .reduce((sum, allocation) => sum + toMoneyNumber(allocation?.allocation_amount), 0);
    return getInvoiceBalance(invoice) + existingAllocation > 0;
};

export const getDefaultPaymentAllocationAmount = (payment, invoice) => {
    const paymentTotal = toMoneyNumber(payment?.total);
    if (paymentTotal <= 0 || Number(payment?.void || 0) !== 0) return '';
    const existingAllocation = (Array.isArray(payment?.allocations) ? payment.allocations : [])
        .filter(allocation => Number(allocation?.invoice_id) === Number(invoice?.id))
        .reduce((sum, allocation) => sum + toMoneyNumber(allocation?.allocation_amount), 0);
    const availableBalance = Math.max(0, getInvoiceBalance(invoice) + existingAllocation);
    const amount = Math.min(paymentTotal, availableBalance);

    return amount > 0 ? amount.toFixed(2) : '';
};

export const getPaymentInvoiceOptionLabel = (invoice) => {
    const number = invoice?.invoice_number || invoice?.id || 'Unnumbered';
    const description = String(invoice?.invoice_description || invoice?.description || '').trim();
    const details = [
        `Invoice #${number}`,
        description,
        `${formatMoney(getInvoiceBalance(invoice))} due`,
        formatStatus(invoice?.status),
    ].filter(Boolean);

    return details.join(' | ');
};

const getAllocationInvoiceLabel = (allocation, invoices) => {
    const invoice = invoices.find(item => Number(item?.id) === Number(allocation?.invoice_id));
    return invoice?.invoice_number || allocation?.invoice_number || allocation?.invoice_id || 'Unknown';
};

export const CreatePayment = ({
    payment,
    setPayment,
    invoices = null,
    errors = {},
    apiError = '',
    disabled = false,
    embedded = false,
}) => {
    const availableInvoices = Array.isArray(invoices) ? invoices : [];
    const allocations = Array.isArray(payment?.allocations) ? payment.allocations : [];
    const allocationMode = payment?._allocationMode || 'preserve';
    const isNegativePayment = toMoneyNumber(payment?.total) < 0;
    const isVoidPayment = Number(payment?.void || 0) !== 0;
    const allocationBlocked = isNegativePayment || isVoidPayment;
    const hasAllocationControls = Array.isArray(invoices);
    const showAllocationControls = hasAllocationControls
        && (!allocationBlocked || allocations.length > 0 || allocationMode !== 'preserve');
    const preservedSingleAllocation = allocationMode === 'preserve' && allocations.length === 1
        ? allocations[0]
        : null;
    const selectedInvoiceId = allocationMode === 'replace'
        ? payment?.invoice_id
        : preservedSingleAllocation?.invoice_id;
    const selectValue = allocationMode === 'preserve' && allocations.length > 1
        ? '__preserve_multiple__'
        : selectedInvoiceId
            ? String(selectedInvoiceId)
            : '';
    const selectedInvoiceMissing = selectedInvoiceId
        && !availableInvoices.some(invoice => Number(invoice?.id) === Number(selectedInvoiceId));
    const selectableInvoices = availableInvoices.filter(invoice => (
        paymentInvoiceIsAllocatable(invoice, payment)
        || Number(invoice?.id) === Number(selectedInvoiceId)
    ));

    const updatePayment = (updates) => {
        setPayment(previous => ({ ...(previous || {}), ...updates }));
    };

    const handleInvoiceChange = (value) => {
        if (allocationBlocked && value !== '') return;

        if (value === '__preserve_multiple__') {
            updatePayment({
                _allocationMode: 'preserve',
                invoice_id: null,
                allocation_amount: null,
            });
            return;
        }

        if (value === '') {
            updatePayment({
                _allocationMode: allocations.length > 0 ? 'clear' : 'preserve',
                invoice_id: null,
                allocation_amount: null,
            });
            return;
        }

        const invoiceId = Number(value);
        const selectedInvoice = availableInvoices.find(invoice => Number(invoice?.id) === invoiceId);
        updatePayment({
            _allocationMode: 'replace',
            invoice_id: invoiceId,
            allocation_amount: getDefaultPaymentAllocationAmount(payment, selectedInvoice),
        });
    };

    const displayedAllocationAmount = allocationMode === 'replace'
        ? payment?.allocation_amount
        : preservedSingleAllocation?.allocation_amount;

    return (
        <div className={embedded ? 'modal-content-section payment-form' : 'modal-content-wrapper payment-form'}>
            {apiError && <div className='payment-form-error' role='alert'>{apiError}</div>}
            <fieldset className='payment-form-fields' disabled={disabled}>
                <div className='form-group alt mid'>
                    <label className='subtext'>Reference #</label>
                    <Text
                        type='text'
                        placeholder='Reference #'
                        value={payment?.reference}
                        onChange={(val) => updatePayment({ reference: val })}
                    />
                    {errors.reference && <span className='payment-field-error'>{errors.reference}</span>}
                </div>
                <div className='form-group alt mid'>
                    <label className='subtext'>Date</label>
                    <DateInput
                        value={payment?.date}
                        onChange={(val) => updatePayment({ date: val })}
                    />
                    {errors.date && <span className='payment-field-error'>{errors.date}</span>}
                </div>
                <div className='form-group alt mid'>
                    <label className='subtext'>Total*</label>
                    <NumberInput
                        type='currency'
                        allowNegative
                        value={payment?.total}
                        onChange={(val) => updatePayment({ total: val })}
                        required={Boolean(errors.total)}
                    />
                    {errors.total && <span className='payment-field-error'>{errors.total}</span>}
                </div>
                <div className='form-group alt mid'>
                    <label className='subtext'>Method*</label>
                    <Text
                        type='text'
                        placeholder='Method*'
                        value={payment?.method}
                        onChange={(val) => updatePayment({ method: val })}
                        required={Boolean(errors.method)}
                    />
                    {errors.method && <span className='payment-field-error'>{errors.method}</span>}
                </div>

                {hasAllocationControls && allocationBlocked && !showAllocationControls && (
                    <div className='payment-allocation-notice' role='status'>
                        {isVoidPayment
                            ? 'Void payments cannot be applied to an invoice.'
                            : 'Refunds are recorded at the case level and cannot be applied to an invoice.'}
                    </div>
                )}

                {showAllocationControls && (
                    <div className='payment-allocation-section'>
                        <div className='form-group payment-allocation-field'>
                            <label className='subtext' htmlFor='payment-invoice-allocation'>Apply to invoice</label>
                            <select
                                id='payment-invoice-allocation'
                                className='default-select payment-invoice-select'
                                value={selectValue}
                                onChange={(event) => handleInvoiceChange(event.target.value)}
                            >
                                <option value=''>No invoice allocation</option>
                                {allocations.length > 1 && (
                                    <option value='__preserve_multiple__' disabled={allocationBlocked}>
                                        Keep {allocations.length} existing allocations ({formatMoney(payment?.allocated_total)})
                                    </option>
                                )}
                                {selectedInvoiceMissing && (
                                    <option value={String(selectedInvoiceId)} disabled={allocationBlocked}>
                                        Invoice #{preservedSingleAllocation?.invoice_number || selectedInvoiceId} (current allocation)
                                    </option>
                                )}
                                {selectableInvoices.map(invoice => {
                                    const allocatable = paymentInvoiceIsAllocatable(invoice, payment);
                                    return (
                                        <option
                                            key={invoice.id}
                                            value={String(invoice.id)}
                                            disabled={allocationBlocked || !allocatable}
                                        >
                                            {getPaymentInvoiceOptionLabel(invoice)}{allocatable ? '' : ' | Not available'}
                                        </option>
                                    );
                                })}
                            </select>
                            {errors.invoice_id && <span className='payment-field-error'>{errors.invoice_id}</span>}
                        </div>

                        {selectedInvoiceId && !allocationBlocked && (
                            <div className='form-group payment-allocation-field'>
                                <label className='subtext' htmlFor='payment-allocation-amount'>Amount applied</label>
                                <div className='number-input'>
                                    <input
                                        id='payment-allocation-amount'
                                        type='number'
                                        inputMode='decimal'
                                        min='0'
                                        step='0.01'
                                        placeholder='0.00'
                                        value={displayedAllocationAmount || ''}
                                        className={errors.allocation_amount ? 'required-input' : ''}
                                        onChange={(event) => updatePayment({
                                            _allocationMode: 'replace',
                                            invoice_id: Number(selectedInvoiceId),
                                            allocation_amount: event.target.value,
                                        })}
                                    />
                                    <span className='number-symbol subtext'>$</span>
                                </div>
                                {errors.allocation_amount && <span className='payment-field-error'>{errors.allocation_amount}</span>}
                            </div>
                        )}

                        {allocationMode === 'preserve' && allocations.length > 1 && (
                            <div className='payment-allocation-summary' aria-label='Existing invoice allocations'>
                                <span className='subtext'>This payment is currently split across:</span>
                                <ul>
                                    {allocations.map(allocation => (
                                        <li key={allocation.id || `${allocation.invoice_id}-${allocation.allocation_amount}`}>
                                            Invoice #{getAllocationInvoiceLabel(allocation, availableInvoices)}: {formatMoney(allocation.allocation_amount)}
                                        </li>
                                    ))}
                                </ul>
                                <span className='subtext'>Saving will keep this split unless you select one invoice or clear it.</span>
                            </div>
                        )}

                        {allocationMode === 'clear' && allocations.length > 0 && (
                            <div className='payment-allocation-notice' role='status'>
                                Saving will remove this payment from its current invoice allocation{allocations.length > 1 ? 's' : ''}.
                            </div>
                        )}

                        {allocationBlocked && allocationMode !== 'clear' && (
                            <div className='payment-allocation-notice' role='status'>
                                {isVoidPayment
                                    ? 'Void payments cannot keep an invoice allocation. Choose No invoice allocation before saving.'
                                    : 'Refunds cannot keep an invoice allocation. Choose No invoice allocation before saving.'}
                            </div>
                        )}
                    </div>
                )}
            </fieldset>
        </div>
    );
};
